import { AnyExtension, Editor } from '@tiptap/core'
import { RestrictedSection } from '../../../../../../backend/src/tiptap/editor/extensions/nodes/restricted-section'
import { createScopeEditor, ScopeEditorHandle } from '../scope-editor'
import { buildCaretUser, CaretUser } from './unique-user-collaboration-caret'

export type RestrictedSectionNodeViewOptions = {
  callId: number
  caretUser: CaretUser
  editable: boolean
  forcePolling: boolean
  /** Builds the host editor's extensions a scope editor shares, such as its citations. */
  buildExtensions: () => AnyExtension[]
  /**
   * The scope editors currently mounted, each with where its mount sits in the base document, so the host can reach
   * them (to rebuild their citations, or search them in reading order).
   */
  scopeEditors: Map<Editor, () => number | undefined>
  /** Called whenever a scope editor mounts or unmounts, so what spans them (a search) can follow. */
  onScopeEditorsChanged: () => void
  /** Shown in place of a restricted item whose scope cannot be resolved. */
  lockedLabel: string
}

function scopeIdOf(attrs: Record<string, unknown>): string | null {
  const templateItemId = attrs['templateItemId'] ?? attrs['scopeId']
  return typeof templateItemId === 'string' && templateItemId !== '' ? templateItemId : null
}

/**
 * Renders a restricted agenda item's mount as a nested collaborative editor bound to its scope document. Only a user
 * authorized for that scope ever loads it, so this is reached only by full-access users editing the base document.
 * A mount without a resolvable scope id renders as an inert locked placeholder rather than mounting an editor.
 */
export const RestrictedSectionNodeView = RestrictedSection.extend<RestrictedSectionNodeViewOptions>({
  addOptions() {
    return {
      callId: 0,
      caretUser: buildCaretUser(null),
      editable: false,
      forcePolling: false,
      buildExtensions: () => [],
      scopeEditors: new Map(),
      onScopeEditorsChanged: () => undefined,
      lockedLabel: '',
    }
  },

  addNodeView() {
    const options = this.options
    return ({ node, getPos }) => {
      const dom = window.document.createElement('div')
      dom.classList.add('scope-editor')

      const scopeId = scopeIdOf(node.attrs)
      const handle: ScopeEditorHandle | null =
        scopeId === null
          ? null
          : createScopeEditor({
              callId: options.callId,
              templateItemId: scopeId,
              element: dom,
              caretUser: options.caretUser,
              editable: options.editable,
              forcePolling: options.forcePolling,
              extensions: options.buildExtensions(),
            })
      if (handle === null) {
        dom.classList.add('scope-editor-locked')
        dom.dataset['lockedLabel'] = options.lockedLabel
      } else {
        options.scopeEditors.set(handle.editor, () => getPos())
        options.onScopeEditorsChanged()
      }

      return {
        dom,
        // The nested editor owns its subtree entirely, so keep the outer editor from reconciling it or stealing events.
        ignoreMutation: () => true,
        stopEvent: () => true,
        update: (updatedNode) => updatedNode.type.name === node.type.name && scopeIdOf(updatedNode.attrs) === scopeId,
        destroy: () => {
          if (handle !== null) {
            options.scopeEditors.delete(handle.editor)
            options.onScopeEditorsChanged()
            handle.destroy()
          }
        },
      }
    }
  },
})
